import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within AuthProvider');
	}
	return context;
};

// Helper function to check if token is expired or invalid
const isTokenExpired = (token) => {
	if (!token) return true;

	try {
		// Check if token has the correct JWT format (3 parts separated by dots)
		const parts = token.split('.');
		if (parts.length !== 3) {
			console.warn('Invalid JWT format - clearing token');
			return true;
		}

		const payload = JSON.parse(atob(parts[1]));
		const expirationTime = payload.exp * 1000; // Convert to milliseconds
		return Date.now() >= expirationTime;
	} catch (error) {
		console.error('Error checking token expiration:', error);
		// If we can't parse the token, it's invalid - clear it
		return true;
	}
};

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		// Load user from localStorage on mount
		const storedToken = localStorage.getItem('token');
		const storedUser = localStorage.getItem('user');

		if (storedToken) {
			// If we have a user, try to parse it
			if (storedUser) {
				// Check if token is expired
				if (isTokenExpired(storedToken)) {
					// Token expired, clear storage
					localStorage.removeItem('token');
					localStorage.removeItem('user');
					setUser(null);
					setIsAuthenticated(false);
				} else {
					// Token valid, restore session
					try {
						const userData = JSON.parse(storedUser);
						setUser(userData);
						setIsAuthenticated(true);
					} catch (e) {
						console.error("Failed to parse stored user", e);
						localStorage.removeItem('user');
						// Optionally fetch user profile here if you had an endpoint for it
					}
				}
			} else {
				// Token exists but no user data. 
				// In a real app we would fetch /api/auth/me here. 
				// For now, we'll just log it.
				console.warn("Token exists but no user data in local storage");
			}
		}

		setLoading(false);

		// Listen for auth:logout event from API interceptor
		const handleLogoutEvent = () => {
			logout();
		};
		window.addEventListener('auth:logout', handleLogoutEvent);

		return () => {
			window.removeEventListener('auth:logout', handleLogoutEvent);
		};
	}, []);

	// Auto-logout when token expires
	useEffect(() => {
		if (!isAuthenticated) return;

		const token = localStorage.getItem('token');
		if (!token) return;

		try {
			const payload = JSON.parse(atob(token.split('.')[1]));
			const expirationTime = payload.exp * 1000;
			const timeUntilExpiration = expirationTime - Date.now();

			if (timeUntilExpiration > 0) {
				// Set timeout to auto-logout when token expires
				const timeoutId = setTimeout(() => {
					logout();
					// Note: User will be redirected to login page, no need for alert
				}, timeUntilExpiration);

				return () => clearTimeout(timeoutId);
			}
		} catch (error) {
			console.error('Error setting up auto-logout:', error);
		}
	}, [isAuthenticated]);

	const login = (userData) => {
		setUser(userData);
		setIsAuthenticated(true);
		localStorage.setItem('token', userData.token);
		localStorage.setItem('user', JSON.stringify(userData));

		// Store login timestamp
		localStorage.setItem('loginTime', Date.now().toString());
	};

	const logout = () => {
		setUser(null);
		setIsAuthenticated(false);
		localStorage.removeItem('token');
		localStorage.removeItem('user');
		localStorage.removeItem('loginTime');
	};

	const value = {
		user,
		isAuthenticated,
		loading,
		login,
		logout
	};

	return (
		<AuthContext.Provider value={value}>
			{!loading && children}
		</AuthContext.Provider>
	);
};
